// ─── Calculs du budget (module PUR — aucun Firebase) ──────────────
// Porté depuis MoPCal `services/budget.js` : ces calculs sont éprouvés en
// production, on les reprend à l'identique. Ils ne connaissent ni Firestore ni
// React — d'où leur testabilité et leur réutilisation par tous les écrans.
//
// Conventions du modèle (cf. MODELE-DONNEES.md § Budget & finances) :
//   • une OPÉRATION porte `sens` ('depense' | 'revenu') et `montant` > 0 ;
//   • `paye: false` = PROVISION (prévue, pas encore réglée) ;
//   • `exclu: true` = hors calcul (virement interne, remboursement) ;
//   • un REMBOURSEMENT est une opération `type:'remboursement'`, `exclu:true`.

// Format financier français : milliers séparés + 2 décimales.
// Ex. 35000 → « 35 000,00 € » ; -1234.5 → « -1 234,50 € ».
export function formatEuro(n) {
  const v = Number(n) || 0;
  const [ent, dec] = Math.abs(v).toFixed(2).split('.');
  return `${v < 0 ? '-' : ''}${ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec} €`;
}

// Montant d'UNE opération — CONVENTION SIGNÉE (Cédric, 15/08, décision
// réaffirmée) : le débit porte « − », le crédit « + ». J'avais recommandé
// l'inverse (un « − » par ligne se confond avec un tiret) ; Cédric a tranché
// en connaissance de cause. L'écart en découle : réel signé − prévu signé,
// positif = favorable au porte-monnaie.
export const montantOperation = (montant, sens) =>
  (sens === 'revenu' ? '+ ' : '− ') + formatEuro(Math.abs(Number(montant) || 0));

// Date qui RANGE l'opération dans le temps : la date banque prime (source de
// vérité), puis le paiement, puis la date de réception de la facture.
export const dateEffective = (d) => (d && (d.dateBanque || d.datePaiement || d.date)) || '';

const cent = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Parts en % des payeurs : normalisées et bornées.
export function normaliserPayeurs(payeurs) {
  return (Array.isArray(payeurs) ? payeurs : [])
    .filter(p => p && p.uid)
    .map(p => ({ uid: p.uid, nom: (p.nom || '').trim(), pct: Math.max(0, Math.min(100, Number(p.pct) || 0)) }));
}
export const totalPourcentages = (payeurs) =>
  cent((payeurs || []).reduce((s, p) => s + (Number(p.pct) || 0), 0));

// ── Récapitulatif d'un budget sur une période ────────────────────
// Le solde de répartition ne concerne QUE les dépenses (qui a payé quoi vs sa
// part) ; les revenus sont comptés à part pour le résultat (revenus − dépenses).
export function recapBudget(operations, payeurs) {
  let totalDep = 0, totalRev = 0;
  const payeParUid = {};
  for (const d of (operations || [])) {
    if (d.exclu) continue;
    const m = d.montant || 0;
    if (d.sens === 'revenu') { totalRev += m; continue; }
    totalDep += m;
    if (d.payeurUid) payeParUid[d.payeurUid] = (payeParUid[d.payeurUid] || 0) + m;
  }
  const lignes = (payeurs || []).map(p => {
    const partDue = cent(totalDep * (Number(p.pct) || 0) / 100);
    const paye = cent(payeParUid[p.uid] || 0);
    return { uid: p.uid, nom: p.nom || '', pct: Number(p.pct) || 0, paye, partDue, solde: cent(paye - partDue) };
  });
  // Réglé par quelqu'un HORS liste de répartition (opération déplacée depuis un
  // autre budget…) : regroupé en ligne « Autres », sinon la somme des soldes ne
  // s'équilibre plus et l'argent semble disparaître.
  if ((payeurs || []).length > 0) {
    const listes = new Set((payeurs || []).map(p => p.uid));
    const reste = cent(Object.entries(payeParUid).filter(([u]) => !listes.has(u)).reduce((s, [, m]) => s + m, 0));
    if (reste > 0.005) lignes.push({ uid: '_autres', nom: '', autres: true, pct: 0, paye: reste, partDue: 0, solde: reste });
  }
  return { total: cent(totalDep), totalRevenus: cent(totalRev), resultat: cent(totalRev - totalDep), lignes };
}

// ── Soldes cumulés par personne (qui doit à qui) ─────────────────
// Ne comptent que le CONFIRMÉ : une provision (`paye:false`) n'est pas une dette.
export function soldesPersonnes(operations, payeurs) {
  let totalDep = 0;
  const paye = {}, transfert = {}, noms = {};
  for (const d of (operations || [])) {
    const m = d.montant || 0;
    if (d.type === 'remboursement') {
      if (d.paye === false) continue;                  // pas encore réglé
      if (d.deUid) { transfert[d.deUid] = (transfert[d.deUid] || 0) + m; if (d.deNom) noms[d.deUid] = d.deNom; }
      if (d.aUid) { transfert[d.aUid] = (transfert[d.aUid] || 0) - m; if (d.aNom) noms[d.aUid] = d.aNom; }
      continue;
    }
    if (d.exclu || d.sens === 'revenu' || d.paye === false) continue;
    totalDep += m;
    if (d.payeurUid) { paye[d.payeurUid] = (paye[d.payeurUid] || 0) + m; if (d.payeurNom) noms[d.payeurUid] = d.payeurNom; }
  }
  const lignes = [];
  const vus = new Set();
  for (const p of (payeurs || [])) {
    const partDue = cent(totalDep * (Number(p.pct) || 0) / 100);
    lignes.push({ uid: p.uid, nom: p.nom || noms[p.uid] || '', solde: cent((paye[p.uid] || 0) + (transfert[p.uid] || 0) - partDue) });
    vus.add(p.uid);
  }
  for (const uid of new Set([...Object.keys(paye), ...Object.keys(transfert)])) {
    if (vus.has(uid)) continue;
    const solde = cent((paye[uid] || 0) + (transfert[uid] || 0));
    if (Math.abs(solde) > 0.005) lignes.push({ uid, nom: noms[uid] || '', solde });
  }
  return lignes;
}

// ── Qui rembourse qui : minimise le NOMBRE de transferts ─────────
// Glouton : on apparie le plus gros débiteur au plus gros créancier.
export function calculerDettes(lignes) {
  const cred = (lignes || []).filter(l => l.solde > 0.005).map(l => ({ nom: l.nom || '—', uid: l.uid, m: l.solde })).sort((a, b) => b.m - a.m);
  const deb = (lignes || []).filter(l => l.solde < -0.005).map(l => ({ nom: l.nom || '—', uid: l.uid, m: -l.solde })).sort((a, b) => b.m - a.m);
  const out = [];
  let i = 0, j = 0;
  while (i < deb.length && j < cred.length) {
    const m = Math.min(deb[i].m, cred[j].m);
    out.push({ de: deb[i].nom, deUid: deb[i].uid, a: cred[j].nom, aUid: cred[j].uid, montant: cent(m) });
    deb[i].m -= m; cred[j].m -= m;
    if (deb[i].m < 0.005) i++;
    if (cred[j].m < 0.005) j++;
  }
  return out;
}

// Total des dépenses d'un jeu d'opérations (hors exclues et revenus).
export const totalBudget = (ops) =>
  cent((ops || []).filter(d => !d.exclu && d.sens !== 'revenu').reduce((s, d) => s + (d.montant || 0), 0));

// Mois 'AAAA-MM' d'une opération, pour le filtrage par période.
export const moisDe = (d) => (dateEffective(d) || '').slice(0, 7);
